/**
 * D3FlexChart — a canvas-based step-after area chart for flexibility band data.
 *
 * Canvas renders the series; a transparent SVG overlay captures mouse events
 * so we can show a tooltip and hover-band without any SVG re-layout costs on
 * every data change.
 *
 * D3MinimapBrush — a compact overview chart with a draggable brush window
 * that controls the visible time range of the main chart.
 */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import * as d3 from 'd3'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface D3FlexChartPoint {
  ts: number // ms epoch — interval start
  endTs: number // ms epoch — interval end
  [key: string]: number | undefined
}

export interface D3FlexChartSeries {
  key: string
  name: string
  color: string
  type: 'area' | 'line' | 'band'
  /** For type='band': the two data keys that form the lower and upper bounds. */
  bandKeys?: [string, string]
  fillOpacity?: number // default 0.2 for area/band
  lineWidth?: number // stroke width override (default: 2 for line, 1.5 for area)
  /**
   * How consecutive points are connected.
   * - 'step-after' (default): horizontal then vertical — suits per-interval instant values.
   * - 'linear': straight line between points, plotted at endTs — suits cumulative totals.
   */
  interpolation?: 'step-after' | 'linear'
  /**
   * Controls how the tooltip displays values for this series.
   * - 'value' (default): shows the point's value.
   * - 'delta': shows "start → end (+Δ)" — useful for cumulative series where the
   *   increase per interval is more informative than the running total alone.
   */
  tooltipMode?: 'value' | 'delta'
  hidden?: boolean
  /**
   * Which y-axis this series is plotted against.
   * - 'left' (default): uses the primary left y-scale.
   * - 'right': uses a secondary right y-scale (independent domain).
   */
  yAxis?: 'left' | 'right'
}

export interface D3FlexChartProps {
  data: D3FlexChartPoint[]
  series: D3FlexChartSeries[]
  unit: string
  /** Unit label for the right y-axis. Only rendered when at least one series has yAxis='right'. */
  rightUnit?: string
  /** If provided, only data within [startMs, endMs] is shown. */
  xDomain?: [number, number]
  height?: number
  /**
   * When true the chart ignores `height` and instead fills 100% of its
   * parent container's height, tracking it via ResizeObserver.
   */
  fillContainer?: boolean
  /**
   * When this value changes the y-axis animation is bypassed and the domain
   * snaps immediately to the new target. Pass a value that toggles whenever
   * a discontinuous context switch occurs (e.g. the chart becomes visible
   * after being hidden) so the lerp doesn't animate across unrelated ranges.
   */
  snapRevision?: unknown
  onSeriesToggle?: (key: string) => void
  /**
   * Externally controlled hover: the `ts` (epoch ms) of the hovered interval.
   * When provided the chart mirrors this hover band without owning mouse state.
   */
  hoveredTs?: number | null
  /** Called when the user hovers over an interval. Pass null on mouse-leave. */
  onHoverChange?: (ts: number | null) => void
  /**
   * Called when the user completes a drag selection over the chart.
   * Receives the selected time range in epoch ms, clamped to xDomain.
   */
  onRangeSelect?: (startMs: number, endMs: number) => void
  /**
   * When provided, tooltip values are read from this dataset instead of `data`.
   * Useful when `data` is a transformed (e.g. normalized) copy and the tooltip
   * should always display the original, non-transformed values.
   * Must be parallel to `data` (same indices, same `ts` values).
   */
  tooltipData?: D3FlexChartPoint[]
}

export interface D3MinimapBrushProps {
  /** Full 48h dataset (all bands). */
  data: D3FlexChartPoint[]
  series: D3FlexChartSeries[]
  /** Current brush window — [startMs, endMs]. */
  brushDomain: [number, number]
  onBrushChange: (startMs: number, endMs: number) => void
  height?: number
}

// ─── Margins ─────────────────────────────────────────────────────────────────

const MARGIN = { top: 10, right: 16, bottom: 40, left: 60 }
const MARGIN_RIGHT_AXIS = { top: 10, right: 64, bottom: 40, left: 60 }
const LEGEND_HEIGHT = 28
const TICK_FONT = '12px sans-serif'

const MM_MARGIN = { top: 4, right: 16, bottom: 20, left: 60 }

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * withOpacity — cached colour → rgba string converter.
 *
 * d3.color() + .rgb() allocates two heap objects and re-parses the colour
 * string on every call.  Series colours are stable at runtime so we memo the
 * result in a Map — the first call per (colour, opacity) pair pays the parse
 * cost; subsequent calls return a pre-built string in O(1) with no allocation.
 */
const _opacityCache = new Map<string, string>()
function withOpacity(colour: string, opacity: number): string {
  // Round opacity to 2 dp to keep the cache bounded
  const opKey = opacity.toFixed(2)
  const key = `${colour}@${opKey}`
  const cached = _opacityCache.get(key)
  if (cached) return cached
  const c = d3.color(colour)
  if (!c) return colour
  const rgb = c.rgb()
  const result = `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`
  _opacityCache.set(key, result)
  return result
}

/** Compute x-tick interval in hours based on visible range. */
function tickIntervalHours(rangeMs: number): number {
  const h = rangeMs / 3_600_000
  if (h <= 6) return 1
  if (h <= 12) return 2
  if (h <= 24) return 4
  return 6
}

/**
 * Generate hourly tick timestamps (epoch ms) for a domain.
 * Returns plain numbers — no Date objects allocated.
 */
function makeXTicks(domainMin: number, domainMax: number, intervalH: number): number[] {
  const ticks: number[] = []
  const intervalMs = intervalH * 3_600_000
  const startDate = new Date(domainMin)
  const startH = Math.ceil(startDate.getHours() / intervalH) * intervalH
  const tickStartDate = new Date(domainMin)
  tickStartDate.setHours(startH, 0, 0, 0)
  for (let t = tickStartDate.getTime(); t <= domainMax; t += intervalMs) {
    ticks.push(t)
  }
  return ticks
}

/** Format an epoch-ms value as HH:MM using en-US locale. */
function fmtHHMM(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

/** Format an epoch-ms value as a short date label, e.g. "Mar 30". */
function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Returns true if the epoch-ms value falls exactly at midnight (00:00:00.000). */
function isMidnight(ms: number): boolean {
  const d = new Date(ms)
  return d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0
}

/**
 * Draw all series onto a canvas context that has already been translated to
 * the plot origin. Returns nothing — pure side-effects.
 *
 * Pixel coordinate arrays are pre-allocated by the caller and passed in as
 * shared scratch buffers to avoid allocating new arrays on every draw call.
 *
 * For sparse series (where a data point has `undefined` for the series key),
 * the series is rendered as disconnected segments — one path per contiguous
 * run of defined values. This lets a "Control Target" line appear only over
 * the intervals that have active control tasks.
 *
 * For type='band', `bandKeys` must be provided as [lowerKey, upperKey]. The
 * fill is drawn between the two bounds with no fill above or below.
 */
function drawSeries(
  ctx: CanvasRenderingContext2D,
  data: D3FlexChartPoint[],
  series: D3FlexChartSeries[],
  xS: d3.ScaleLinear<number, number>,
  yS: d3.ScaleLinear<number, number>,
  /** Reusable scratch buffer — must be at least data.length * 2 entries. */
  pxBuf: Float64Array,
  /** Optional right y-scale. Used for series with yAxis='right'. */
  ySRight?: d3.ScaleLinear<number, number>
): void {
  for (const s of series) {
    if (s.hidden) continue

    // Pick the appropriate y-scale for this series
    const yScale = s.yAxis === 'right' && ySRight ? ySRight : yS

    // ── Band series: filled region between two keys ─────────────────────────
    if (s.type === 'band' && s.bandKeys) {
      const [loKey, hiKey] = s.bandKeys

      // Identify contiguous runs where both keys are defined.
      // We draw each run as a closed fill path then stroke hi and lo bounds.
      let runStart = -1
      for (let i = 0; i <= data.length; i++) {
        const d = data[i]
        const defined =
          d !== undefined &&
          d[loKey] !== undefined &&
          isFinite(d[loKey]!) &&
          d[hiKey] !== undefined &&
          isFinite(d[hiKey]!)

        if (defined && runStart === -1) {
          runStart = i
        } else if (!defined && runStart !== -1) {
          // Flush the run [runStart, i)
          const runLen = i - runStart
          // Fill pxBuf: hi points at [0..runLen*2), lo points at [runLen*2..runLen*4)
          for (let j = 0; j < runLen; j++) {
            const rd = data[runStart + j]
            pxBuf[j * 2] = xS(rd.ts)
            pxBuf[j * 2 + 1] = yScale(rd[hiKey]!)
            pxBuf[runLen * 2 + j * 2] = xS(rd.ts)
            pxBuf[runLen * 2 + j * 2 + 1] = yScale(rd[loKey]!)
          }
          const lastEndPx = xS(data[runStart + runLen - 1].endTs)

          // Fill path: forward along hi, then reverse along lo
          ctx.save()
          ctx.beginPath()
          ctx.moveTo(pxBuf[0], pxBuf[1])
          for (let j = 1; j < runLen; j++) {
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 - 1]) // step right at old y
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1]) // drop to new y
          }
          ctx.lineTo(lastEndPx, pxBuf[(runLen - 1) * 2 + 1]) // extend to interval end
          // Walk back along lo (reverse step-after)
          ctx.lineTo(lastEndPx, pxBuf[runLen * 2 + (runLen - 1) * 2 + 1])
          for (let j = runLen - 1; j >= 1; j--) {
            ctx.lineTo(pxBuf[runLen * 2 + j * 2], pxBuf[runLen * 2 + j * 2 + 1])
            ctx.lineTo(pxBuf[runLen * 2 + j * 2], pxBuf[runLen * 2 + (j - 1) * 2 + 1])
          }
          ctx.lineTo(pxBuf[runLen * 2], pxBuf[runLen * 2 + 1])
          ctx.closePath()
          ctx.fillStyle = withOpacity(s.color, s.fillOpacity ?? 0.2)
          ctx.fill()
          ctx.restore()

          // Stroke hi bound
          ctx.save()
          ctx.strokeStyle = s.color
          ctx.lineWidth = 1.5
          ctx.beginPath()
          ctx.moveTo(pxBuf[0], pxBuf[1])
          for (let j = 1; j < runLen; j++) {
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 - 1])
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1])
          }
          ctx.lineTo(lastEndPx, pxBuf[(runLen - 1) * 2 + 1])
          ctx.stroke()
          // Stroke lo bound
          ctx.beginPath()
          ctx.moveTo(pxBuf[runLen * 2], pxBuf[runLen * 2 + 1])
          for (let j = 1; j < runLen; j++) {
            ctx.lineTo(pxBuf[runLen * 2 + j * 2], pxBuf[runLen * 2 + j * 2 - 1])
            ctx.lineTo(pxBuf[runLen * 2 + j * 2], pxBuf[runLen * 2 + j * 2 + 1])
          }
          ctx.lineTo(lastEndPx, pxBuf[runLen * 2 + (runLen - 1) * 2 + 1])
          ctx.stroke()
          ctx.restore()

          runStart = -1
        }
      }
      continue
    }

    // ── Area / line series ──────────────────────────────────────────────────

    const linear = s.interpolation === 'linear'

    // Identify contiguous runs of defined values for this key and draw them.
    let runStart = -1
    for (let i = 0; i <= data.length; i++) {
      const d = data[i]
      const v = d?.[s.key]
      const defined = v !== undefined && isFinite(v)

      if (defined && runStart === -1) {
        runStart = i
      } else if (!defined && runStart !== -1) {
        const runLen = i - runStart
        // Fill pxBuf: x at [j*2], y at [j*2+1]
        for (let j = 0; j < runLen; j++) {
          const rd = data[runStart + j]
          pxBuf[j * 2] = xS(linear ? rd.endTs : rd.ts)
          pxBuf[j * 2 + 1] = yScale(rd[s.key]!)
        }
        const lastEndPx = xS(data[runStart + runLen - 1].endTs)
        const y0 = yScale(0)

        if (s.type === 'area') {
          ctx.save()
          ctx.beginPath()
          if (linear) {
            ctx.moveTo(pxBuf[0], pxBuf[1])
            for (let j = 1; j < runLen; j++) ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1])
          } else {
            ctx.moveTo(pxBuf[0], pxBuf[1])
            for (let j = 1; j < runLen; j++) {
              ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 - 1])
              ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1])
            }
            ctx.lineTo(lastEndPx, pxBuf[(runLen - 1) * 2 + 1])
          }
          ctx.lineTo(lastEndPx, y0)
          ctx.lineTo(pxBuf[0], y0)
          ctx.closePath()
          ctx.fillStyle = withOpacity(s.color, s.fillOpacity ?? 0.2)
          ctx.fill()
          ctx.restore()
        }

        // Stroke
        ctx.save()
        ctx.beginPath()
        if (linear) {
          ctx.moveTo(pxBuf[0], pxBuf[1])
          for (let j = 1; j < runLen; j++) ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1])
        } else {
          ctx.moveTo(pxBuf[0], pxBuf[1])
          for (let j = 1; j < runLen; j++) {
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 - 1])
            ctx.lineTo(pxBuf[j * 2], pxBuf[j * 2 + 1])
          }
          ctx.lineTo(lastEndPx, pxBuf[(runLen - 1) * 2 + 1])
        }
        ctx.strokeStyle = s.color
        ctx.lineWidth = s.lineWidth ?? (s.type === 'line' ? 2 : 1.5)
        ctx.stroke()
        ctx.restore()

        runStart = -1
      }
    }
  }
}

// ─── Main chart ──────────────────────────────────────────────────────────────

export default function D3FlexChart({
  data,
  series,
  unit,
  rightUnit,
  xDomain,
  height: heightProp = 250,
  fillContainer = false,
  snapRevision,
  onSeriesToggle,
  hoveredTs,
  onHoverChange,
  onRangeSelect,
  tooltipData,
}: D3FlexChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Separate lightweight canvas just for the hover band — redrawn on every
  // mousemove without touching the main canvas.
  const hoverCanvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<SVGSVGElement>(null)

  const [width, setWidth] = useState(600)
  const [measuredHeight, setMeasuredHeight] = useState(heightProp)

  // The effective height: measured from the container when fillContainer, else the prop.
  const height = fillContainer ? measuredHeight : heightProp

  // Use wider right margin when any series has a right y-axis.
  const hasRightAxis = series.some((s) => s.yAxis === 'right' && !s.hidden)
  const margin = hasRightAxis ? MARGIN_RIGHT_AXIS : MARGIN

  const [tooltip, setTooltip] = useState<{
    x: number
    y: number
    point: D3FlexChartPoint
  } | null>(null)

  // Drag-to-select state — kept in a ref so mousemove handler on window never
  // captures a stale closure.
  const dragRef = useRef<{
    startClientX: number
    startMs: number
    currentMs: number
  } | null>(null)
  // Separate state so the hover canvas re-renders during drag.
  const [dragRange, setDragRange] = useState<[number, number] | null>(null)

  // ── Animated y-domain ─────────────────────────────────────────────────────
  // Instead of jumping to the new y-domain instantaneously (which causes harsh
  // steps when the brush window or the baseline blend slider moves), we lerp the
  // displayed domain toward the target on every animation frame.  All animation
  // state lives in refs so the rAF loop never captures stale closures and never
  // triggers React re-renders for intermediate frames.
  const animYDomainRef = useRef<[number, number] | null>(null)
  const targetYDomainRef = useRef<[number, number] | null>(null)
  const rafRef = useRef<number | null>(null)

  // Keep a ref-copy of xDomain so drawCanvas always reads the latest value
  // without needing xDomain in its useCallback dep array.  This means panning
  // the brush (which only changes xDomain) does NOT recreate drawCanvas and
  // does NOT re-trigger the y-domain animation effect — it just schedules one
  // extra draw via the xDomain-only effect below.
  const xDomainRef = useRef(xDomain)
  useEffect(() => {
    xDomainRef.current = xDomain
  })

  // ── Canvas size tracking — avoid reallocating the bitmap on every draw ────
  // Setting canvas.width / canvas.height releases and reallocates the entire
  // backing bitmap, producing a large GC allocation at 60 fps during animation.
  // We track the last physical size we committed and skip the assignment when
  // the dimensions haven't changed.
  const canvasSizeRef = useRef<{ w: number; h: number } | null>(null)
  const hoverCanvasSizeRef = useRef<{ w: number; h: number } | null>(null)

  // ── Pixel coordinate scratch buffer ───────────────────────────────────────
  // drawSeries needs temporary storage for pixel coordinates. Allocating new
  // arrays per draw (at 60 fps during animation) is a significant source of GC
  // pressure. We allocate one Float64Array per chart instance and grow it as
  // needed; it is reused across all draws.
  // Size: we need 4 * data.length entries for band series (hi + lo, x + y per
  // point). data.length is stable between draws so the buffer rarely grows.
  const pxBufRef = useRef<Float64Array>(new Float64Array(0))
  const ensurePxBuf = useCallback((minLen: number) => {
    if (pxBufRef.current.length < minLen) {
      pxBufRef.current = new Float64Array(minLen * 2) // over-allocate to avoid frequent grows
    }
  }, [])

  // Resolve which interval is currently highlighted.
  // External hoveredTs takes precedence; fall back to local tooltip.
  const activeHoveredTs = hoveredTs !== undefined ? hoveredTs : (tooltip?.point.ts ?? null)

  const plotW = Math.max(1, width - margin.left - margin.right)
  const plotH = Math.max(1, height - margin.top - margin.bottom - LEGEND_HEIGHT)

  // ── ResizeObserver ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      const w = rect.width
      if (w && w > 0) setWidth((prev) => (prev === Math.floor(w) ? prev : Math.floor(w)))
      if (fillContainer) {
        const h = rect.height
        if (h && h > 0) setMeasuredHeight((prev) => (prev === Math.floor(h) ? prev : Math.floor(h)))
      }
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [fillContainer])

  // ── Build scales ─────────────────────────────────────────────────────────
  const buildXScale = useCallback((): d3.ScaleLinear<number, number> => {
    if (data.length === 0) return d3.scaleLinear().domain([0, 1]).range([0, plotW])
    const xd = xDomainRef.current
    const domainMin = xd ? xd[0] : (d3.min(data, (d) => d.ts) ?? 0)
    const domainMax = xd ? xd[1] : data[data.length - 1].endTs
    return d3.scaleLinear().domain([domainMin, domainMax]).range([0, plotW])
  }, [data, plotW])

  /** Compute the target [yMin, yMax] from the visible data — no .nice() so ticks
   * don't snap the domain to discrete round values. */
  const computeTargetYDomain = useCallback((): [number, number] => {
    if (data.length === 0) return [0, 1]
    const xd = xDomainRef.current
    const visibleData = xd ? data.filter((d) => d.ts < xd[1] && d.endTs > xd[0]) : data
    if (visibleData.length === 0) return [0, 1]
    // Exclude right-axis series from the left y-domain
    const visibleSeries = series.filter((s) => !s.hidden && s.yAxis !== 'right')
    const allKeys = visibleSeries.flatMap((s) =>
      s.type === 'band' && s.bandKeys ? s.bandKeys : [s.key]
    )
    if (allKeys.length === 0) return [0, 1]
    const allVals = visibleData
      .flatMap((d) => allKeys.map((k) => d[k]))
      .filter((v): v is number => v !== undefined && isFinite(v))
    const yMax = d3.max(allVals) ?? 1
    const yMin = d3.min(allVals) ?? 0
    const padding = (yMax - yMin) * 0.05 || 0.05
    return [yMin - padding, yMax + padding]
  }, [data, series, xDomain])

  /** Compute the right y-domain from visible right-axis series. */
  const computeRightYDomain = useCallback((): [number, number] => {
    if (data.length === 0) return [0, 1]
    const xd = xDomainRef.current
    const visibleData = xd ? data.filter((d) => d.ts < xd[1] && d.endTs > xd[0]) : data
    if (visibleData.length === 0) return [0, 1]
    const rightSeries = series.filter((s) => !s.hidden && s.yAxis === 'right')
    const allKeys = rightSeries.flatMap((s) =>
      s.type === 'band' && s.bandKeys ? s.bandKeys : [s.key]
    )
    if (allKeys.length === 0) return [0, 1]
    const allVals = visibleData
      .flatMap((d) => allKeys.map((k) => d[k]))
      .filter((v): v is number => v !== undefined && isFinite(v))
    const yMax = d3.max(allVals) ?? 1
    const yMin = d3.min(allVals) ?? 0
    const padding = (yMax - yMin) * 0.05 || 0.05
    return [yMin - padding, yMax + padding]
  }, [data, series, xDomain])

  /** Build a y-scale from an already-computed [yMin, yMax] domain. */
  const buildYScaleFrom = useCallback(
    ([yMin, yMax]: [number, number]): d3.ScaleLinear<number, number> => {
      return d3.scaleLinear().domain([yMin, yMax]).range([plotH, 0])
    },
    [plotH]
  )

  // ── Canvas draw function — called both from the main effect and the rAF loop ─
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const canvasH = height - LEGEND_HEIGHT
    const physW = Math.round(width * dpr)
    const physH = Math.round(canvasH * dpr)

    // Only reallocate the backing bitmap when the physical pixel size changes.
    // Setting canvas.width/height on every frame releases and reallocates the
    // entire bitmap buffer, generating large GC pressure at 60 fps.
    const cs = canvasSizeRef.current
    if (!cs || cs.w !== physW || cs.h !== physH) {
      canvas.width = physW
      canvas.height = physH
      canvasSizeRef.current = { w: physW, h: physH }
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, width, canvasH)

    const xS = buildXScale()
    // animYDomainRef is always non-null by the time drawCanvas runs: it is
    // initialised on first render in the main effect before drawCanvas is
    // called, and never reset to null again (snapRevision resets it to null
    // then immediately re-initialises it in its own effect).  The fallback
    // to computeTargetYDomain() is therefore a dead branch — we keep it only
    // as a last-resort safety net, not as a regular hot-path call.
    const yDom = animYDomainRef.current ?? computeTargetYDomain()
    const yS = buildYScaleFrom(yDom)

    // Build right y-scale if any visible series uses the right axis
    const rightSeriesVisible = series.filter((s) => !s.hidden && s.yAxis === 'right')
    const ySRight =
      rightSeriesVisible.length > 0 ? buildYScaleFrom(computeRightYDomain()) : undefined

    ctx.save()
    ctx.translate(margin.left, margin.top)

    // Grid + Y ticks (left axis)
    const yTicks = yS.ticks(5)
    ctx.font = TICK_FONT
    ctx.fillStyle = 'rgba(128,128,128,0.85)'
    ctx.strokeStyle = 'rgba(128,128,128,0.12)'
    ctx.lineWidth = 1
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    yTicks.forEach((t) => {
      const py = yS(t)
      ctx.beginPath()
      ctx.moveTo(0, py)
      ctx.lineTo(plotW, py)
      ctx.stroke()
      ctx.fillText(t.toFixed(t < 10 ? 1 : 0), -8, py)
    })

    // Y-axis unit label — rotated 90°, centred on the axis height (matches scatter plot style)
    if (unit) {
      ctx.save()
      ctx.translate(-36, plotH / 2)
      ctx.rotate(-Math.PI / 2)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.globalAlpha = 0.6
      ctx.fillText(unit, 0, 0)
      ctx.restore()
    }

    // Right y-axis ticks and label (when a right scale is present)
    if (ySRight && rightUnit) {
      const yTicksRight = ySRight.ticks(5)
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = 'rgba(128,128,128,0.85)'
      ctx.strokeStyle = 'rgba(128,128,128,0.06)'
      ctx.lineWidth = 1
      yTicksRight.forEach((t) => {
        const py = ySRight(t)
        ctx.fillText(t.toFixed(t < 10 ? 2 : 1), plotW + 8, py)
      })
      // Right axis label
      ctx.save()
      ctx.translate(plotW + margin.right - 10, plotH / 2)
      ctx.rotate(Math.PI / 2)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.globalAlpha = 0.6
      ctx.fillText(rightUnit, 0, 0)
      ctx.restore()
    }

    // X ticks — makeXTicks now returns epoch-ms numbers (no Date objects)
    const xDom = xS.domain()
    const intervalH = tickIntervalHours(xDom[1] - xDom[0])
    const xTicks = makeXTicks(xDom[0], xDom[1], intervalH)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    xTicks.forEach((t) => {
      const px = xS(t)
      ctx.strokeStyle = isMidnight(t) ? 'rgba(128,128,128,0.28)' : 'rgba(128,128,128,0.12)'
      ctx.lineWidth = isMidnight(t) ? 1.5 : 1
      ctx.beginPath()
      ctx.moveTo(px, 0)
      ctx.lineTo(px, plotH)
      ctx.stroke()
      ctx.lineWidth = 1
      ctx.fillStyle = 'rgba(128,128,128,0.85)'
      if (isMidnight(t)) {
        // Two-line label: date on the first line, "00:00" below
        ctx.font = '11px sans-serif'
        ctx.fillStyle = 'rgba(128,128,128,1)'
        ctx.fillText(fmtDate(t), px, plotH + 6)
        ctx.font = TICK_FONT
        ctx.fillStyle = 'rgba(128,128,128,0.7)'
        ctx.fillText(fmtHHMM(t), px, plotH + 19)
      } else {
        ctx.font = TICK_FONT
        ctx.fillText(fmtHHMM(t), px, plotH + 8)
      }
    })

    // Clip + series
    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, plotW, plotH)
    ctx.clip()
    let visibleData: D3FlexChartPoint[]
    const xd = xDomainRef.current
    if (xd) {
      const firstVisibleIdx = data.findIndex((d) => d.ts < xd[1] && d.endTs > xd[0])
      const hasLinear = series.some((s) => s.interpolation === 'linear' && !s.hidden)
      const startIdx = hasLinear && firstVisibleIdx > 0 ? firstVisibleIdx - 1 : firstVisibleIdx
      visibleData =
        firstVisibleIdx === -1 ? [] : data.filter((d, i) => i >= startIdx && d.ts < xd[1])
    } else {
      visibleData = data
    }
    // Ensure the scratch buffer is large enough for this draw's dataset.
    // band series need 4 entries per point (hi x, hi y, lo x, lo y).
    ensurePxBuf(visibleData.length * 4)
    drawSeries(ctx, visibleData, series, xS, yS, pxBufRef.current, ySRight)
    ctx.restore()

    // Axes border (left + bottom; right axis border when applicable)
    ctx.strokeStyle = 'rgba(128,128,128,0.35)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(0, plotH)
    ctx.lineTo(plotW, plotH)
    ctx.stroke()
    if (ySRight) {
      ctx.beginPath()
      ctx.moveTo(plotW, 0)
      ctx.lineTo(plotW, plotH)
      ctx.stroke()
    }

    ctx.restore()
  }, [
    data,
    series,
    width,
    height,
    margin,
    buildXScale,
    computeTargetYDomain,
    computeRightYDomain,
    buildYScaleFrom,
    plotW,
    plotH,
    ensurePxBuf,
    unit,
    rightUnit,
  ])

  // ── xDomain-only redraw — pan/zoom without touching y-domain animation ────
  // When only the brush window changes (xDomain), we just need to redraw the
  // canvas with the new x-scale.  The y-domain animation loop is NOT restarted
  // — it continues or is idle — so no extra lerp frames are scheduled.
  // xDomainRef is already updated by its own effect (no dep on drawCanvas),
  // so here we only call drawCanvas after confirming animYDomain is initialised.
  useEffect(() => {
    if (animYDomainRef.current === null) return // first draw handled by main effect
    drawCanvas()
  }, [xDomain, drawCanvas])

  // ── Main canvas: series + grid — NOT redrawn on hover ─────────────────────
  // Runs when data/series/dimensions change: recomputes the y-domain target and
  // starts the lerp animation loop toward it.
  // When snapRevision changes the current animated domain is discarded so the
  // next draw snaps immediately without lerping from a stale range.
  useEffect(() => {
    const target = computeTargetYDomain()
    targetYDomainRef.current = target

    // Initialise animYDomain on first render, or snap when snapRevision changes
    if (animYDomainRef.current === null) {
      animYDomainRef.current = target
      drawCanvas()
      return
    }

    // Cancel any in-flight animation
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    const LERP = 0.35 // per-frame blend factor — snappier, fewer frames total
    const THRESHOLD = 5e-3 // stop when within 0.5% of range — cuts the slow tail

    function tick() {
      const anim = animYDomainRef.current!
      const tgt = targetYDomainRef.current!
      const newMin = anim[0] + (tgt[0] - anim[0]) * LERP
      const newMax = anim[1] + (tgt[1] - anim[1]) * LERP
      animYDomainRef.current = [newMin, newMax]
      drawCanvas()
      const deltaMin = Math.abs(tgt[0] - newMin)
      const deltaMax = Math.abs(tgt[1] - newMax)
      const range = Math.abs(tgt[1] - tgt[0]) || 1
      if (deltaMin / range > THRESHOLD || deltaMax / range > THRESHOLD) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        // Snap to exact target and do one final draw
        animYDomainRef.current = tgt
        drawCanvas()
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [computeTargetYDomain, drawCanvas, snapRevision])

  // When snapRevision changes, discard the current animated domain so the next
  // draw effect snaps directly to the new target instead of lerping from the
  // previous card's range.
  useEffect(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    animYDomainRef.current = null
    // drawCanvas will be called by the main effect which also reruns on snapRevision
  }, [snapRevision])

  // ── Hover canvas: resize when dimensions change, paint when tooltip changes ─
  // Separating these means a window resize doesn't need tooltip, and a tooltip
  // change doesn't need to reset canvas dimensions.
  useEffect(() => {
    const canvas = hoverCanvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const canvasH = height - LEGEND_HEIGHT
    const physW = Math.round(width * dpr)
    const physH = Math.round(canvasH * dpr)
    const cs = hoverCanvasSizeRef.current
    if (!cs || cs.w !== physW || cs.h !== physH) {
      canvas.width = physW
      canvas.height = physH
      hoverCanvasSizeRef.current = { w: physW, h: physH }
    }
  }, [width, height])

  useEffect(() => {
    const canvas = hoverCanvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    // Reset to identity before clearing so we cover the full physical bitmap.
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.translate(margin.left, margin.top)
    const xS = buildXScale()

    // Draw drag selection rect (takes priority over hover band while dragging)
    if (dragRange) {
      const dx1 = Math.max(0, Math.min(xS(dragRange[0]), xS(dragRange[1])))
      const dx2 = Math.min(plotW, Math.max(xS(dragRange[0]), xS(dragRange[1])))
      ctx.fillStyle = 'rgba(99,102,241,0.18)'
      ctx.fillRect(dx1, 0, dx2 - dx1, plotH)
      ctx.strokeStyle = 'rgba(99,102,241,0.7)'
      ctx.lineWidth = 1.5
      ctx.strokeRect(dx1, 0, dx2 - dx1, plotH)
    } else if (activeHoveredTs != null) {
      // Draw hover band for hovered interval
      const point = data.find((d) => d.ts <= activeHoveredTs && d.endTs > activeHoveredTs)
      if (point) {
        const hx1 = Math.max(0, xS(point.ts))
        const hx2 = Math.min(plotW, xS(point.endTs))
        ctx.fillStyle = 'rgba(99,102,241,0.15)'
        ctx.fillRect(hx1, 0, hx2 - hx1, plotH)
      }
    }

    ctx.restore()
  }, [activeHoveredTs, dragRange, data, width, height, buildXScale, plotW, plotH, margin])

  // ── Memoize the sorted ts array so bisect doesn't allocate on every mousemove
  const tsSorted = useMemo(() => data.map((d) => d.ts), [data])

  // The dataset used for tooltip value display — falls back to `data` when no
  // override is provided (i.e. when the chart is not showing transformed values).
  const resolvedTooltipData = tooltipData ?? data

  // ── Mouse interaction ──────────────────────────────────────────────────────

  /** Convert a client-x position on the SVG overlay to a clamped epoch-ms value. */
  const clientXToMs = useCallback(
    (clientX: number): number => {
      const svg = overlayRef.current
      if (!svg) return 0
      const rect = svg.getBoundingClientRect()
      const mx = clientX - rect.left - margin.left
      const xS = buildXScale()
      const ts = xS.invert(mx)
      const [domMin, domMax] = xS.domain()
      return Math.max(domMin, Math.min(domMax, ts))
    },
    [buildXScale, margin]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!onRangeSelect) return
      e.preventDefault()
      const startMs = clientXToMs(e.clientX)
      dragRef.current = { startClientX: e.clientX, startMs, currentMs: startMs }
      setDragRange([startMs, startMs])
      // Suppress hover/tooltip while dragging
      setTooltip(null)
      onHoverChange?.(null)

      const onMove = (me: MouseEvent) => {
        if (!dragRef.current) return
        const currentMs = clientXToMs(me.clientX)
        dragRef.current.currentMs = currentMs
        setDragRange([dragRef.current.startMs, currentMs])
      }

      const onUp = (_me: MouseEvent) => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        if (!dragRef.current) return
        const { startMs, currentMs } = dragRef.current
        dragRef.current = null
        setDragRange(null)
        const lo = Math.min(startMs, currentMs)
        const hi = Math.max(startMs, currentMs)
        // Only fire if the drag covered at least 1 minute
        if (hi - lo > 60_000) onRangeSelect(lo, hi)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [onRangeSelect, clientXToMs, onHoverChange]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      // Suppress hover updates while dragging
      if (dragRef.current) return
      if (data.length === 0) return
      const svg = overlayRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const mx = e.clientX - rect.left - margin.left
      const xS = buildXScale()
      const tsAtMouse = xS.invert(mx)

      const idx = d3.bisectLeft(tsSorted, tsAtMouse)
      const i = Math.max(0, Math.min(idx - 1, data.length - 1))
      const point = data[i]
      if (!point) return
      if (
        tsAtMouse > point.endTs ||
        (xDomain && (point.endTs <= xDomain[0] || point.ts >= xDomain[1]))
      ) {
        setTooltip(null)
        onHoverChange?.(null)
        return
      }
      setTooltip({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        point: resolvedTooltipData[i] ?? point,
      })
      onHoverChange?.(point.ts)
    },
    [data, tsSorted, buildXScale, xDomain, onHoverChange, margin]
  )

  const handleMouseLeave = useCallback(() => {
    if (dragRef.current) return
    setTooltip(null)
    onHoverChange?.(null)
  }, [onHoverChange])

  const canvasH = height - LEGEND_HEIGHT

  return (
    <div
      ref={containerRef}
      className={`relative w-full select-none${fillContainer ? ' h-full' : ''}`}
    >
      {/* Main series canvas */}
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: canvasH,
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />
      {/* Hover band canvas — sits above series, below SVG overlay */}
      <canvas
        ref={hoverCanvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: canvasH,
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
      {/* Spacer so the div has the right height */}
      <div style={{ height: canvasH }} />

      {/* SVG overlay — captures mouse */}
      <svg
        ref={overlayRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: canvasH,
          cursor: dragRange ? 'ew-resize' : 'crosshair',
        }}
        onMouseDown={onRangeSelect ? handleMouseDown : undefined}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />

      {/* Legend */}
      <div
        className="flex flex-wrap items-center justify-center gap-4 text-xs"
        style={{ height: LEGEND_HEIGHT, paddingLeft: margin.left, paddingRight: margin.right }}
      >
        {series.map((s) => (
          <button
            key={s.key}
            onClick={() => onSeriesToggle?.(s.key)}
            className="flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 text-foreground"
            style={{ opacity: s.hidden ? 0.35 : 1 }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: s.type === 'line' ? 2 : 10,
                marginTop: s.type === 'line' ? 5 : 0,
                background:
                  s.type === 'band' ? withOpacity(s.color, s.fillOpacity ?? 0.25) : s.color,
                border: s.type === 'band' ? `1.5px solid ${s.color}` : 'none',
                borderRadius: 1,
              }}
            />
            {s.name}
          </button>
        ))}
      </div>

      {tooltip && (
        <TooltipBox
          x={tooltip.x}
          y={tooltip.y}
          point={tooltip.point}
          prevPoint={resolvedTooltipData[tsSorted.indexOf(tooltip.point.ts) - 1] ?? null}
          series={series}
          unit={unit}
          rightUnit={rightUnit}
          containerWidth={width}
        />
      )}
    </div>
  )
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface TooltipBoxProps {
  x: number
  y: number
  point: D3FlexChartPoint
  prevPoint: D3FlexChartPoint | null
  series: D3FlexChartSeries[]
  unit: string
  rightUnit?: string
  containerWidth: number
}

function TooltipBox({
  x,
  y,
  point,
  prevPoint,
  series,
  unit,
  rightUnit,
  containerWidth,
}: TooltipBoxProps) {
  const tooltipW = 240
  const leftPos = x + 12 + tooltipW > containerWidth ? x - tooltipW - 12 : x + 12

  const fmtTime = (ms: number) =>
    new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })

  const fmtVal = (v: number | undefined, seriesUnit: string) =>
    typeof v === 'number' ? `${v.toFixed(2)} ${seriesUnit}` : 'N/A'

  // Build the interval header: always include the date so it's clear which day
  // the hovered interval belongs to (the chart spans up to 48 hours).
  const startDate = new Date(point.ts)
  const endDate = new Date(point.endTs)
  const startDay = fmtDate(point.ts)
  const endDay = fmtDate(point.endTs)
  const sameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  const intervalHeader = sameDay
    ? `${startDay}, ${fmtTime(point.ts)} – ${fmtTime(point.endTs)}`
    : `${startDay} ${fmtTime(point.ts)} – ${endDay} ${fmtTime(point.endTs)}`

  return (
    <div
      className="absolute pointer-events-none z-50 rounded-lg border bg-popover text-popover-foreground shadow-md text-sm"
      style={{ top: Math.max(0, y - 10), left: leftPos, minWidth: 160, padding: '10px 12px' }}
    >
      <p className="font-semibold mb-1.5">{intervalHeader}</p>
      {series
        .filter((s) => {
          if (s.hidden) return false
          if (s.type === 'band' && s.bandKeys) {
            return s.bandKeys.some((k) => point[k] !== undefined)
          }
          return point[s.key] !== undefined
        })
        .map((s) => {
          const seriesUnit = s.yAxis === 'right' ? (rightUnit ?? unit) : unit
          if (s.type === 'band' && s.bandKeys) {
            const [loKey, hiKey] = s.bandKeys
            return (
              <div key={s.key} className="flex items-center gap-1.5 mb-0.5">
                <span style={{ color: s.color, fontSize: 10 }}>▬</span>
                <span className="flex-1">{s.name.replace(/ \[.*\]$/, '')}:</span>
                <span className="tabular-nums">
                  {fmtVal(point[loKey] as number, seriesUnit)} –{' '}
                  {fmtVal(point[hiKey] as number, seriesUnit)}
                </span>
              </div>
            )
          }
          if (s.tooltipMode === 'delta') {
            const endVal = point[s.key] as number
            const startVal = (prevPoint?.[s.key] as number | undefined) ?? 0
            const delta = endVal - startVal
            const sign = delta >= 0 ? '+' : ''
            return (
              <div key={s.key} className="flex items-center gap-1.5 mb-0.5">
                <span style={{ color: s.color, fontSize: 10 }}>●</span>
                <span className="flex-1">{s.name.replace(/ \[.*\]$/, '')}:</span>
                <span className="tabular-nums">
                  {startVal.toFixed(2)} → {endVal.toFixed(2)}{' '}
                  <span className="text-muted-foreground">
                    ({sign}
                    {delta.toFixed(2)}) {seriesUnit}
                  </span>
                </span>
              </div>
            )
          }
          return (
            <div key={s.key} className="flex items-center gap-1.5 mb-0.5">
              <span style={{ color: s.color, fontSize: 10 }}>●</span>
              <span className="flex-1">{s.name.replace(/ \[.*\]$/, '')}:</span>
              <span className="tabular-nums">
                {typeof point[s.key] === 'number'
                  ? `${(point[s.key] as number).toFixed(2)} ${seriesUnit}`
                  : 'N/A'}
              </span>
            </div>
          )
        })}
    </div>
  )
}

// ─── Minimap Brush ───────────────────────────────────────────────────────────

const BRUSH_HANDLE_W = 8 // px width of each drag handle

type DragTarget = 'start' | 'end' | 'window' | null

export function D3MinimapBrush({
  data,
  series,
  brushDomain,
  onBrushChange,
  height = 64,
}: D3MinimapBrushProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // bgCanvasRef — series + ticks, only redrawn when data/series/dimensions change
  const bgCanvasRef = useRef<HTMLCanvasElement>(null)
  // brushCanvasRef — brush rect + handles only, redrawn on every brushDomain change
  const brushCanvasRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<SVGSVGElement>(null)

  const [width, setWidth] = useState(600)

  const plotW = Math.max(1, width - MM_MARGIN.left - MM_MARGIN.right)
  const plotH = Math.max(1, height - MM_MARGIN.top - MM_MARGIN.bottom)

  // Keep brushDomain in a ref so drag handlers always have the latest value
  // without needing it in useCallback dep arrays (which would recreate handlers
  // on every brush change and re-register window event listeners).
  const brushDomainRef = useRef(brushDomain)
  useEffect(() => {
    brushDomainRef.current = brushDomain
  })

  // Drag state — kept in a ref to avoid stale closures in event handlers
  const dragRef = useRef<{
    target: DragTarget
    startMouseX: number
    startBrush: [number, number]
  } | null>(null)

  // rAF handle for throttling onBrushChange notifications to React
  const notifyRafRef = useRef<number | null>(null)
  // Latest computed brush during a drag — flushed to React on the next rAF
  const pendingBrushRef = useRef<[number, number] | null>(null)

  // Canvas size tracking — avoid reallocating the bitmap on every brush draw
  const bgCanvasSizeRef = useRef<{ w: number; h: number } | null>(null)
  const brushCanvasSizeRef = useRef<{ w: number; h: number } | null>(null)

  // Pixel coordinate scratch buffer for drawSeries in the minimap background
  const mmPxBufRef = useRef<Float64Array>(new Float64Array(0))
  const ensureMmPxBuf = useCallback((minLen: number) => {
    if (mmPxBufRef.current.length < minLen) {
      mmPxBufRef.current = new Float64Array(minLen * 2)
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w && w > 0) setWidth((prev) => (prev === Math.floor(w) ? prev : Math.floor(w)))
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // ── Scales ────────────────────────────────────────────────────────────────
  // Stable across brush changes — only data/series/dimensions affect scales.
  const buildScales = useCallback(() => {
    const xMin = data.length > 0 ? (d3.min(data, (d) => d.ts) ?? 0) : 0
    const xMax = data.length > 0 ? data[data.length - 1].endTs : 1
    const xS = d3.scaleLinear().domain([xMin, xMax]).range([0, plotW])

    const visibleSeries = series.filter((s) => !s.hidden)
    const allKeys = visibleSeries.flatMap((s) =>
      s.type === 'band' && s.bandKeys ? s.bandKeys : [s.key]
    )
    const allVals =
      allKeys.length > 0
        ? data
            .flatMap((d) => allKeys.map((k) => d[k]))
            .filter((v): v is number => v !== undefined && isFinite(v))
        : [0, 1]
    const yMax = d3.max(allVals) ?? 1
    const yMin = d3.min(allVals) ?? 0
    const padding = (yMax - yMin) * 0.05 || 0.05
    const yS = d3
      .scaleLinear()
      .domain([yMin - padding, yMax + padding])
      .range([plotH, 0])

    return { xS, yS }
  }, [data, series, plotW, plotH])

  // Keep a ref to the current xScale so the brush overlay can use it without
  // rebuilding scales on every brush repaint.
  const xScaleRef = useRef<d3.ScaleLinear<number, number> | null>(null)

  // ── Background canvas — series + x-axis ticks ────────────────────────────
  // Only redrawn when data/series/dimensions change, NOT on brush moves.
  useEffect(() => {
    const canvas = bgCanvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const physW = Math.round(width * dpr)
    const physH = Math.round(height * dpr)
    const cs = bgCanvasSizeRef.current
    if (!cs || cs.w !== physW || cs.h !== physH) {
      canvas.width = physW
      canvas.height = physH
      bgCanvasSizeRef.current = { w: physW, h: physH }
    }

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, width, height)

    const { xS, yS } = buildScales()
    xScaleRef.current = xS

    ctx.save()
    ctx.translate(MM_MARGIN.left, MM_MARGIN.top)

    // Clip + series
    ctx.save()
    ctx.beginPath()
    ctx.rect(0, 0, plotW, plotH)
    ctx.clip()
    const dimmedSeries = series.map((s) => ({ ...s, fillOpacity: 0.12, hidden: false }))
    ensureMmPxBuf(data.length * 4)
    drawSeries(ctx, data, dimmedSeries, xS, yS, mmPxBufRef.current)
    ctx.restore()

    // X axis ticks — epoch-ms numbers, no Date allocations
    const xDom = xS.domain()
    const xTicks = makeXTicks(xDom[0], xDom[1], 6)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    xTicks.forEach((t) => {
      const px = xS(t)
      if (isMidnight(t)) {
        // At midnight: show short date label
        ctx.font = '10px sans-serif'
        ctx.fillStyle = 'rgba(128,128,128,0.9)'
        ctx.fillText(fmtDate(t), px, plotH + 2)
      } else {
        ctx.font = '10px sans-serif'
        ctx.fillStyle = 'rgba(128,128,128,0.7)'
        ctx.fillText(fmtHHMM(t), px, plotH + 3)
      }
    })

    ctx.restore()
  }, [data, series, width, height, buildScales, plotW, plotH, ensureMmPxBuf])

  // ── Brush overlay canvas — dim + rect + handles ───────────────────────────
  // Lightweight: only depends on brushDomain and dimensions.
  const drawBrushOverlay = useCallback(
    (bd: [number, number]) => {
      const canvas = brushCanvasRef.current
      if (!canvas) return
      const xS = xScaleRef.current
      if (!xS) return
      const dpr = window.devicePixelRatio || 1
      const physW = Math.round(width * dpr)
      const physH = Math.round(height * dpr)

      // Only reallocate when dimensions change — not on every brush move
      const cs = brushCanvasSizeRef.current
      if (!cs || cs.w !== physW || cs.h !== physH) {
        canvas.width = physW
        canvas.height = physH
        brushCanvasSizeRef.current = { w: physW, h: physH }
      }

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)

      ctx.save()
      ctx.translate(MM_MARGIN.left, MM_MARGIN.top)

      const bx1 = Math.max(0, xS(bd[0]))
      const bx2 = Math.min(plotW, xS(bd[1]))

      // Dim outside brush
      ctx.fillStyle = 'rgba(128,128,128,0.25)'
      ctx.fillRect(0, 0, bx1, plotH)
      ctx.fillRect(bx2, 0, plotW - bx2, plotH)

      // Brush border
      ctx.strokeStyle = 'rgba(99,102,241,0.8)'
      ctx.lineWidth = 1.5
      ctx.strokeRect(bx1, 0, bx2 - bx1, plotH)

      // Handles
      ctx.fillStyle = 'rgba(99,102,241,0.9)'
      const hw = BRUSH_HANDLE_W
      const hh = Math.min(plotH * 0.6, 24)
      const hy = (plotH - hh) / 2
      ctx.beginPath()
      ctx.roundRect(bx1 - hw / 2, hy, hw, hh, 3)
      ctx.fill()
      ctx.beginPath()
      ctx.roundRect(bx2 - hw / 2, hy, hw, hh, 3)
      ctx.fill()

      ctx.restore()
    },
    [width, height, plotW, plotH]
  )

  useEffect(() => {
    drawBrushOverlay(brushDomain)
  }, [brushDomain, drawBrushOverlay])

  // ── Drag logic ────────────────────────────────────────────────────────────
  const getTarget = useCallback(
    (mx: number): DragTarget => {
      const xS = xScaleRef.current
      if (!xS) return null
      const bd = brushDomainRef.current
      const bx1 = xS(bd[0])
      const bx2 = xS(bd[1])
      const plotMx = mx - MM_MARGIN.left

      if (Math.abs(plotMx - bx1) <= BRUSH_HANDLE_W) return 'start'
      if (Math.abs(plotMx - bx2) <= BRUSH_HANDLE_W) return 'end'
      if (plotMx > bx1 && plotMx < bx2) return 'window'
      return null
    },
    [] // no deps — reads only refs
  )

  const getCursor = useCallback(
    (mx: number): string => {
      const t = getTarget(mx)
      if (t === 'start' || t === 'end') return 'ew-resize'
      if (t === 'window') return 'grab'
      return 'default'
    },
    [getTarget]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = overlayRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const target = getTarget(mx)
      if (!target) return
      e.preventDefault()

      const xS = xScaleRef.current!
      const totalMs = (xS.domain()[1] - xS.domain()[0]) as number
      const fullMin = xS.domain()[0]
      const fullMax = xS.domain()[1]

      dragRef.current = {
        target,
        startMouseX: e.clientX,
        startBrush: [...brushDomainRef.current] as [number, number],
      }

      const onMove = (me: MouseEvent) => {
        if (!dragRef.current) return
        const { target: t, startMouseX, startBrush } = dragRef.current
        const dx = me.clientX - startMouseX
        const dMs = (dx / plotW) * totalMs

        let [s, en] = startBrush
        const minWindow = 15 * 60 * 1000
        if (t === 'start') {
          s = Math.max(fullMin, Math.min(s + dMs, en - minWindow))
        } else if (t === 'end') {
          en = Math.min(fullMax, Math.max(en + dMs, s + minWindow))
        } else if (t === 'window') {
          const span = en - s
          s = Math.max(fullMin, Math.min(s + dMs, fullMax - span))
          en = s + span
        }
        const newBrush: [number, number] = [s, en]

        // Draw the brush overlay immediately — no React state update needed
        drawBrushOverlay(newBrush)
        brushDomainRef.current = newBrush
        pendingBrushRef.current = newBrush

        // Throttle the React state notification to one per animation frame
        if (notifyRafRef.current === null) {
          notifyRafRef.current = requestAnimationFrame(() => {
            notifyRafRef.current = null
            if (pendingBrushRef.current) {
              onBrushChange(pendingBrushRef.current[0], pendingBrushRef.current[1])
              pendingBrushRef.current = null
            }
          })
        }
      }

      const onUp = () => {
        // Flush any pending notification immediately on mouse-up
        if (notifyRafRef.current !== null) {
          cancelAnimationFrame(notifyRafRef.current)
          notifyRafRef.current = null
        }
        if (pendingBrushRef.current) {
          onBrushChange(pendingBrushRef.current[0], pendingBrushRef.current[1])
          pendingBrushRef.current = null
        }
        dragRef.current = null
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [getTarget, onBrushChange, plotW, drawBrushOverlay]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = overlayRef.current
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      svg.style.cursor = getCursor(e.clientX - rect.left)
    },
    [getCursor]
  )

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {/* Static background: series + x-axis ticks */}
      <canvas ref={bgCanvasRef} style={{ display: 'block', width: '100%', height }} />
      {/* Dynamic overlay: brush rect + handles — painted directly in drag handler */}
      <canvas
        ref={brushCanvasRef}
        style={{
          display: 'block',
          width: '100%',
          height,
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
      <svg
        ref={overlayRef}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      />
    </div>
  )
}
